import { PageTitle, RevealAnim } from '@okam/brand-ui/index'
import { isHex } from '@okam/core-lib/index'
import { useHtml2React } from '@okam/html2react/provider'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import { useState } from 'react'
import { css } from 'twin.macro'
import { MainLayout } from 'components/layout'
import type { TDirectusProps } from 'components/layout/MainLayout'
import ProductsListing from 'components/products-listing/ProductsListing'
import Seo from 'components/Seo'
import type { SeoContentFragment } from 'gql/graphql'
import { QueryKeys } from 'lib/react-query'
import useAnimalArchivePage from 'lib/react-query/useAnimalArchivePage'
import { useColor } from 'providers/Color'

export interface TAnimalArchivePage {
  animalType: string
  locale: string
  seo: { seo: SeoContentFragment | null | undefined; pageTitle: string }
  directusProps: TDirectusProps
}

const AnimalArchivePage = ({ animalType, locale, seo, directusProps }: TAnimalArchivePage) => {
  const { setColorScheme } = useColor()

  const { data, isSuccess } = useAnimalArchivePage(
    [QueryKeys.GetAnimalArchivePage, animalType],
    locale,
    animalType,
    (animalArchiveColorScheme) => {
      setColorScheme({
        title: animalArchiveColorScheme?.title || 'Test',
        accent: isHex(animalArchiveColorScheme?.accent) ? animalArchiveColorScheme?.accent ?? '#000000' : '#000000',
        background: isHex(animalArchiveColorScheme?.background)
          ? animalArchiveColorScheme?.background ?? '#000000'
          : '#000000',
      })
    },
  )
  const { t } = useTranslation('common')
  const router = useRouter()

  const pageTitle = t('TITLE.ARCHIVE', {
    animal: `<strong>${data?.animals.translations?.[0]?.friendly_title?.toUpperCase()}</strong>`,
  })

  const [exitingPage, setExitingPage] = useState<boolean | undefined>(undefined)

  const handleCardOpened = () => {
    setExitingPage(true)
  }

  const backgroundColor = data?.pages.colorScheme?.background
  const logoColor = data?.pages.colorScheme?.accent

  const { parseHTMLToReact } = useHtml2React()

  return (
    <>
      <Seo directusData={directusProps} seo={seo?.seo} pageTitle={seo?.pageTitle} />
      <RevealAnim mode="popLayout" route={router.asPath} exit={exitingPage}>
        <MainLayout footerProps={directusProps} logoColor={logoColor}>
          {isSuccess && (
            <>
              <PageTitle
                css={css`
                  background-color: ${backgroundColor};
                `}
              >
                <span>{parseHTMLToReact(pageTitle)}</span>
              </PageTitle>
              <ProductsListing
                queryKey={QueryKeys.GetAnimalProductsListing}
                locale={locale}
                animalType={animalType}
                onCardOpened={handleCardOpened}
              />
            </>
          )}
        </MainLayout>
      </RevealAnim>
    </>
  )
}

export default AnimalArchivePage
